'use client'

import { useNotification } from '@/utils/notifications'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import * as z from 'zod'
import { SupportLink } from './SupportLink'

const authenticationErrors = {
  access_denied: (
    <>
      Error encountered during login. Please check you&apos;ve entered the
      correct organization ID or <SupportLink />.
    </>
  ),
  invalid_request: (
    <>
      Error encountered during login. Your invitation may have expired or may
      have been revoked. Please request your SELECT admin to send you a new
      invitation or <SupportLink />.
    </>
  ),
}

const generalAuthError = (
  <>
    Error encountered. Please try again or <SupportLink />.
  </>
)

const authErrorParamSchema = z.object({
  auth_error: z.string(),
})

export function useAuthenticationToast() {
  const { showNotification } = useNotification()
  const router = useRouter()
  const searchParams = useSearchParams()

  // Grab the "auth_error" from URL params
  const authError = searchParams?.get('auth_error')

  useEffect(() => {
    if (!authError) return

    const result = authErrorParamSchema.safeParse({ auth_error: authError })

    if (result.success) {
      showNotification({
        isError: true,
        title: `Authentication failed: ${result.data.auth_error}`,
        body:
          authenticationErrors[
            result.data.auth_error as keyof typeof authenticationErrors
          ] || generalAuthError,
      })

      // Remove "auth_error" from the URL
      const newParams = new URLSearchParams(searchParams?.toString())
      newParams.delete('auth_error')

      const newUrl = `${window.location.pathname}?${newParams.toString()}`
      router.replace(newUrl)
    }
  }, [authError, router, searchParams, showNotification])
}

export default { useAuthenticationToast }
